import {
  Button,
  CardContent,
  Flex,
  Grid,
  Icon,
  Image,
  Link,
  Option,
  Select,
  SummaryItem,
  Text,
  Tooltip,
} from '@pega/cosmos-react-core';
import date from 'date-and-time';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as Logos from '../constants/images';
import { ReleaseNotesLink } from '../constants/common';
import useLocalTranslation from '../hooks/useLocalTranslation';
import { accountInfo, appState, genericObject } from '../interfaces/common';
import { fetchAllAccounts, updateCurrentTab } from '../redux/actions';
import { usePlatformVersionSelector } from '../redux/selectors';
import { tabConfig } from './SocialMedia/Chat/Tabs/DisplayToggles';
import { InteractiveText, StyledCard } from './Utilities/custom.styled';
import SectionRenderTemplate from './Utilities/SectionRenderTemplate';
import { ShowEncryptionDisableTooltip } from './Utilities/ShowEncryptionDisableTooltip';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const translate = useLocalTranslation();
  const [filteredPlatform, setfilteredPlatform] = useState('');
  const dispatch = useDispatch();
  const isClientPublicKeyPresent = useSelector((state: appState) => state.isClientPublickeyPresent);
  const { majorVersion, minorVersion } = usePlatformVersionSelector();
  const beyondEightDotEight =
    majorVersion && (majorVersion > 8 || (majorVersion === 8 && minorVersion && minorVersion >= 8));

  const { accounts, sessionId } = useSelector((state: appState) => state);
  const [linkedSdkConnections, setLinkedSdkConnections] = useState([String]);
  useEffect(() => {
    const tempSdkConnections: any = [];
    accounts.forEach((account) => {
      if (account.syncConnectionId && account.sdkPlatform === 'sdk_mobile')
        tempSdkConnections.push(account?.syncConnectionId);
    });
    setLinkedSdkConnections(tempSdkConnections);
  }, [accounts]);
  const itemRefs: any = useRef([]);
  const isFbTestSession = sessionId === 'dms_fb_test';
  const categoryImageContent: genericObject = {
    facebook: Logos.FacebookLogo,
    twitter: Logos.TwitterLogo,
    abc: Logos.ABCLogo,
    twilio: Logos.SMSLogo,
    chat: Logos.ChatLogo,
    'twilio-whatsapp': Logos.WhatsappLogo,
    sdk_mobile: Logos.SDKLogo,
    custom: Logos.CustomChannelLogo,
    'vonage-sms': Logos.SMSLogo,
    'vonage-whatsapp': Logos.WhatsappLogo,
    agent_trainer: Logos.CustomerSimulatorLogo,
    combined_messaging: Logos.CombinedMessagingLogo,
  };
  const convertDate = (lastupdated: string) => {
    return date.format(new Date(lastupdated), 'DD MMM YYYY HH:mm:ss');
  };
  const routeToPath = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate],
  );

  useEffect(() => {
    dispatch(fetchAllAccounts());
    // Set to Default General
    dispatch(updateCurrentTab(tabConfig.General));
  }, [dispatch]);

  const getPlatformNames = () => {
    let connectionPlatforms: string[] = accounts.map((item: accountInfo) => item.standardPlatformName);
    return Array.from(new Set(connectionPlatforms));
  };
  const filteredAccounts =
    accounts &&
    accounts
      .filter((item: accountInfo) => !filteredPlatform || item.standardPlatformName === filteredPlatform)
      .sort((a: accountInfo, b: accountInfo) => (new Date(b.lastUpdate) > new Date(a.lastUpdate) ? 1 : -1));

  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, filteredAccounts.length);
  }, [filteredAccounts]);

  const handleRef = (element: Element, index: number) => {
    itemRefs.current[index] = element;
  };

  const createColumnData = () => {
    return filteredAccounts.map((connectionItem, i) => {
      const key = `${i}`;
      const isSdkLinked = linkedSdkConnections?.includes(connectionItem.syncConnectionId as any);
      return (
        <Flex key={key}>
          {process.env.REACT_APP_ENABLE_VONAGE !== '1' &&
          ['vonage-sms', 'vonage-whatsapp'].includes(connectionItem.platform) ? (
            <></>
          ) : (
            <StyledCard
              className="pointer"
              borderPositionCSS="border-bottom"
              key={key}
              onClick={() =>
                !isSdkLinked &&
                routeToPath(
                  connectionItem.sdkPlatform
                    ? `mobile/sdk_mobile/${connectionItem.connectionId}`
                    : connectionItem.combinedMessaging
                    ? `unified-channel/combined_messaging/${connectionItem.connectionId}`
                    : `/${connectionItem.platform}/${connectionItem.connectionId}`,
                )
              }
              style={{ borderRadius: 0 }}
              ref={(element: Element) => handleRef(element, i)}
            >
              <CardContent style={{ paddingLeft: 0 }}>
                {connectionItem.sdkPlatform === 'sdk_mobile' && isSdkLinked && (
                  <Tooltip target={itemRefs.current[i]} key={key + 1}>
                    {translate(
                      'disabled_sdk_tooltip',
                      accounts.find((data) => data.connectionId === connectionItem?.syncConnectionId)?.title ?? '',
                    )}
                  </Tooltip>
                )}
                <SummaryItem
                  visual={
                    <Image
                      alt={connectionItem.platform}
                      src={
                        categoryImageContent[
                          connectionItem.sdkPlatform ||
                            connectionItem.combinedMessaging ||
                            (connectionItem.isCustomerSimulator && 'agent_trainer') ||
                            connectionItem.platform
                        ]
                      }
                      style={{ objectFit: 'fill' }}
                    />
                  }
                  primary={<InteractiveText isDisabled={isSdkLinked}>{connectionItem.title}</InteractiveText>}
                  secondary={
                    <Text variant="secondary">
                      {`${translate('last_updated')} ${convertDate(connectionItem.lastUpdate)}`}
                    </Text>
                  }
                  actions={
                    beyondEightDotEight &&
                    !isClientPublicKeyPresent &&
                    connectionItem.encryptionEnabled && <ShowEncryptionDisableTooltip />
                  }
                />
              </CardContent>
            </StyledCard>
          )}
        </Flex>
      );
    });
  };

  const headerElement = () => {
    return (
      <Flex
        container={{
          direction: 'row',
          justify: 'between',
          alignItems: 'end',
        }}
      >
        <Text variant="h1" as="h1">
          {translate('connections_heading')}
        </Text>
        <Button onClick={() => routeToPath('/add-connection')} variant="primary">
          {translate('add_connection_button')}
        </Button>
      </Flex>
    );
  };

  return (
    <>
      <SectionRenderTemplate hideNavigation={true} header={headerElement()}>
        {!isFbTestSession && (
          <>
            <Grid
              container={{
                cols: 'auto auto',
                justifyItems: 'start',
                alignItems: 'center',
              }}
            >
              <Select
                label={translate('filter_by_type_label')}
                value={filteredPlatform}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  setfilteredPlatform(e.target.value);
                }}
              >
                <Option value="">{translate('all_connections')}</Option>
                {getPlatformNames()
                  .sort()
                  .map((item, index) => {
                    return <Option key={index}>{translate(item)}</Option>;
                  })}
              </Select>
              <Link style={{ justifySelf: 'end' }} href={ReleaseNotesLink} variant="link" target="_blank">
                {translate('release_notes')}
                <Icon name="open" style={{ marginLeft: '0.5rem' }} aria-label="Informative label" />
              </Link>
            </Grid>
            <Flex>{createColumnData()}</Flex>
          </>
        )}
      </SectionRenderTemplate>
    </>
  );
};

export default Home;
